import { Mail, FileText, Download, ArrowUpRight, BadgeCheck } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { CTABand } from "../../components/site/CTABand";
import { Stagger, StaggerItem, Reveal } from "../../components/motion/Reveal";
import { CONTACT } from "../../data/content";

// Brand catalogue hub — to add a new brand, append one object here; the layout needs no changes.
// file: null = placeholder until the brand's catalogue PDF link is provided.
// links: external brochure/resource pages provided by the brand.
const BRAND_CATALOGUES = [
  { slug: "hikvision", name: "Hikvision", text: "Video Surveillance and Security Solutions", file: null, logo: "/brands/hikvision.png", dealer: true, links: [
    { label: "All Products Catalogue", href: "https://www.hikvision.com/in/products/" },
    { label: "Download Center", href: "https://www.hikvision.com/in/support/download/" },
  ] },
  { slug: "matrix", name: "Matrix", text: "Communication and Security Solutions", file: null, logo: null, links: [
    { label: "All Technical Specifications", href: "https://www.matrixcomsec.com/resources/technical-specifications/" },
    { label: "Access Control & Time Attendance", href: "https://www.matrixcomsec.com/resources/technical-specifications/#accesscontroltimeattendance" },
    { label: "Telecom", href: "https://www.matrixcomsec.com/resources/technical-specifications/#Telecom" },
  ] },
  { slug: "zkteco", name: "ZKTeco", text: "Biometric and Access Control Solutions", file: "/catalogues/zkteco-product-catalogue-2026.pdf", logo: null },
  { slug: "tplink", name: "TP-Link", text: "Networking and Wi-Fi Solutions", file: null, logo: "/brands/tplink.svg" },
];

const DOCUMENTS = [
  { name: "Solution Brochures", text: "Per-solution overviews: infrastructure, networking, Wi-Fi, surveillance, access, automation and more." },
  { name: "Service Brochures", text: "The full service lifecycle — consulting to commissioning, AMC and managed services." },
  { name: "AMC Information", text: "What our annual maintenance covers, response commitments and reporting." },
  { name: "Technical Documents", text: "Standards, specifications and design references where relevant to your project." },
];

export default function Downloads() {
  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="downloads-page">
      <SiteHeader />
      <PageHero
        crumb="Downloads"
        eyebrow="Download Centre"
        title="Product Catalogues &"
        titleAccent="Resources"
        description="Download catalogues from our authorized brand partners."
      />
      <section className="py-16 sm:py-24" data-testid="catalogue-section">
        <div className="nx-wrap">
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BRAND_CATALOGUES.map((b) => (
              <StaggerItem key={b.slug}>
                <div className="flex flex-col h-full border border-white/10 bg-nx-navy2 p-7 hover:border-nx-gold/40 transition-colors duration-300" data-testid={`catalogue-card-${b.slug}`}>
                  <div className="flex items-center justify-center h-20 bg-white px-6 mb-6" data-testid={`catalogue-logo-${b.slug}`}>
                    {b.logo ? (
                      <img src={b.logo} alt={`${b.name} logo`} className="max-h-10 max-w-[70%] object-contain" loading="lazy" />
                    ) : (
                      <span className="font-archivo font-bold text-xl tracking-wide text-neutral-900">{b.name}</span>
                    )}
                  </div>
                  {b.dealer && (
                    <span className="inline-flex items-center gap-1.5 self-start mb-3 border border-nx-gold/50 bg-nx-gold/10 text-nx-goldlight text-[10px] font-semibold tracking-[0.18em] uppercase px-3 py-1" data-testid={`catalogue-badge-${b.slug}`}>
                      <BadgeCheck size={12} /> Authorized Dealer
                    </span>
                  )}
                  <h2 className="font-marcellus text-lg text-white mb-3">{b.name}</h2>
                  <p className="text-sm text-slate-400 leading-relaxed flex-1">{b.text}</p>
                  {b.links && (
                    <div className="flex flex-col gap-2 mt-5">
                      {b.links.map((l, i) => (
                        <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-nx-goldlight transition-colors" data-testid={`catalogue-link-${b.slug}-${i}`}>
                          <ArrowUpRight size={14} className="text-nx-goldlight shrink-0" /> {l.label}
                        </a>
                      ))}
                    </div>
                  )}
                  {b.file ? (
                    <a href={b.file} download className="group mt-6 inline-flex items-center justify-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3 transition-colors" data-testid={`catalogue-download-${b.slug}`}>
                      <Download size={14} /> Download Catalogue
                    </a>
                  ) : b.links ? (
                    <a href={b.links[0].href} target="_blank" rel="noopener noreferrer" className="group mt-6 inline-flex items-center justify-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3 transition-colors" data-testid={`catalogue-download-${b.slug}`}>
                      <ArrowUpRight size={14} /> View Brochures
                    </a>
                  ) : (
                    <a href="#" onClick={(e) => e.preventDefault()} title="Catalogue link will be added shortly" className="group mt-6 inline-flex items-center justify-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3 transition-colors" data-testid={`catalogue-download-${b.slug}`}>
                      <Download size={14} /> Download Catalogue
                    </a>
                  )}
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      <section className="pb-16 sm:pb-24" data-testid="documents-section">
        <div className="nx-wrap">
          <Reveal>
            <div className="flex items-center gap-4 mb-4">
              <span className="h-px w-12 bg-nx-gold/70" />
              <span className="text-[10px] sm:text-xs tracking-[0.16em] sm:tracking-[0.24em] uppercase text-nx-goldlight">Company Documents</span>
            </div>
            <h2 className="font-marcellus text-2xl sm:text-3xl text-white leading-tight max-w-2xl mb-4">NexSecure documents, on request.</h2>
            <p className="text-sm text-slate-400 max-w-2xl leading-relaxed mb-12">Our brochures and corporate documents are shared directly so you always receive the current version. Tell us which document you need and it lands in your inbox.</p>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {DOCUMENTS.map((d, i) => (
              <StaggerItem key={d.name}>
                <a href={`mailto:${CONTACT.sales}?subject=${encodeURIComponent(`Document request: ${d.name}`)}`} className="group flex flex-col h-full bg-nx-navy hover:bg-nx-navy2 p-7 transition-colors" data-testid={`download-doc-${i}`}>
                  <FileText size={22} className="text-nx-goldlight mb-5" strokeWidth={1.5} />
                  <h3 className="font-marcellus text-lg text-white mb-2">{d.name}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed flex-1">{d.text}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-nx-goldlight">
                    <Mail size={13} /> Request by email
                  </span>
                </a>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      <CTABand title="Need documents for a tender or proposal?" text="Send us the tender reference and we'll prepare the right document pack for it." />
      <SiteFooter />
    </div>
  );
}
